// apps/widget/src/router.tsx
//
// Two entry points, not a general router:
//   /w/:key         the scan flow, inside the iframe
//   /m/:sessionId   mobile capture, opened from a QR code
//
// The mobile route is a completely separate flow — it captures and uploads, and
// the report appears on the desktop rather than the phone.

import { useEffect, useState } from 'react';
import type { WidgetConfig } from './lib/api';
import { useScan, type Step } from './state';
import { setVoice, t } from './lib/i18n';
import { listen, send } from './lib/postmessage';
import { api } from './lib/api';

import { WelcomeScreen } from './screens/welcome.screen';
import { ConsentScreen } from './screens/consent.screen';
import { IntakeScreen } from './screens/intake.screen';
import { PrepareScreen } from './screens/prepare.screen';
import { CaptureScreen } from './screens/capture.screen';
import { QrHandoffScreen } from './screens/qr-handoff.screen';
import { MobileCaptureScreen } from './screens/mobile-capture.screen';
import { AnalyzingScreen } from './screens/analyzing.screen';
import { ReportScreen } from './screens/report.screen';
import { RecommendationsScreen } from './screens/recommendations.screen';
import { RoutineScreen } from './screens/routine.screen';
import { LeadCaptureScreen } from './screens/lead-capture.screen';
import { BookingScreen } from './screens/booking.screen';
import { ErrorScreen } from './screens/error.screen';
import { WidgetHeader } from './components/widget-header';

export function Router({ config }: { config: WidgetConfig }) {
  const { state, go, dispatch } = useScan();
  const [confirmClose, setConfirmClose] = useState(false);
  const [route] = useState(() => parseRoute());

  // Only the opening screen is a landscape split. Telling the host lets it
  // narrow the dialog for everything after it, instead of leaving a single
  // column marooned in a panel twice its width.
  useEffect(() => {
    // Capture gets its own, larger panel: the viewfinder is the content, and
    // a column sized for reading text makes a poor camera.
    const mode =
      state.step === 'welcome' ||
      state.step === 'consent' ||
      state.step === 'intake' ||
      state.step === 'prepare' ||
      state.step === 'report'
        ? 'wide'
        : state.step === 'capture' || state.step === 'analysing'
          ? 'capture'
          : 'narrow';
    send('gs:layout', { mode });
  }, [state.step]);

  // Past the welcome screen there is work to lose, so the host stops closing
  // on its own and asks here instead.
  useEffect(() => {
    send('gs:guard', { active: state.step !== 'welcome' });
  }, [state.step]);

  useEffect(() => listen({ onCloseRequested: () => setConfirmClose(true) }), []);

  // Clinic staff opening this report from their dashboard: /w/<key>?staff=<token>.
  // The token is short-lived and names one scan; it is the only way to the
  // kept photo. The report itself is exactly what the patient saw.
  useEffect(() => {
    const token = new URLSearchParams(location.search).get('staff');
    if (!token) return;
    void api
      .staffView(token)
      .then(({ report, face }) => {
        // Written about the patient, not to them, for the staff reading it.
        setVoice('staff');
        dispatch({
          type: 'staffReport',
          report,
          capture: face ? { photoUrl: face.photoUrl, width: face.width, height: face.height, landmarks: face.landmarks } : null,
        });
      })
      .catch(() =>
        dispatch({
          type: 'error',
          error: { code: 'NOT_FOUND', message: 'This report link has expired. Open the scan again from your dashboard.' },
        }),
      );
  }, [dispatch]);

  // A shared or emailed report link: /w/<key>?report=<shortId>. The key is in
  // the path because every public endpoint needs it — the old /r/<shortId>
  // link carried none, so it could never load.
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get('report');
    if (!ref) return;
    void api
      .report(ref)
      .then((report) => dispatch({ type: 'report', report }))
      .catch(() =>
        dispatch({
          type: 'error',
          error: { code: 'NOT_FOUND', message: 'This report could not be found.' },
        }),
      );
  }, [dispatch]);

  // No widget header in staff view, so nothing is laid out beneath one.
  useEffect(() => {
    document.documentElement.toggleAttribute('data-staff-view', state.staffView);
  }, [state.staffView]);

  // Mobile capture is its own flow and shares none of the desktop chrome.
  if (route.kind === 'mobile') {
    return <MobileCaptureScreen sessionId={route.sessionId} token={route.token} config={config} />;
  }

  return (
    <>
      {/* Framed in the clinic's dashboard, the dashboard's own drawer is the
          header — the widget's would be a second one, with a clinic name staff
          already know and nothing to close. */}
      {!state.staffView && (
        <WidgetHeader
          config={config}
          onBack={BACK_TO[state.step] ? () => go(BACK_TO[state.step]!) : undefined}
          onClose={() => (state.step === 'welcome' ? send('gs:close') : setConfirmClose(true))}
        />
      )}

      {config.isTestMode && !state.staffView && (
        <div className="gs-test-banner" role="status">
          {t('common.testMode')}
        </div>
      )}

      <div className="gs-screen">
        {state.step === 'error' && state.error ? (
          // Every failure lands here with its own code, message and fixes. With
          // no case for it, all of them — a bad photo included — fell through
          // to a generic "Something went wrong" with no way to retake.
          <ErrorScreen
            code={state.error.code}
            message={state.error.message}
            {...(state.error.fixes ? { fixes: state.error.fixes } : {})}
            onRetry={() => go(state.session ? 'capture' : 'welcome')}
          />
        ) : (
          renderStep(state.step)
        )}
      </div>

      {confirmClose && (
        <div className="gs-confirm" role="alertdialog" aria-modal="true" aria-labelledby="gs-confirm-title">
          <div className="gs-confirm__panel">
            <h2 className="gs-title" id="gs-confirm-title">{t('close.title')}</h2>
            <p className="gs-body">{t('close.body')}</p>
            <div className="gs-stack">
              <button className="gs-btn gs-btn--primary" onClick={() => setConfirmClose(false)}>
                {t('close.stay')}
              </button>
              <button className="gs-btn gs-btn--ghost" onClick={() => send('gs:close')}>
                {t('close.leave')}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

/** Where back goes from each step. Absent means the step has no way back. */
const BACK_TO: Record<string, Step | undefined> = {
  consent: 'welcome',
  intake: 'consent',
  prepare: 'intake',
  capture: 'prepare',
  qr: 'capture',
  // Taken from the per-screen buttons this replaced, so no route changed.
  recommendations: 'report',
  routine: 'recommendations',
  booking: 'recommendations',
  lead: 'report',
};

function renderStep(step: string) {
  switch (step) {
    case 'welcome':
      return <WelcomeScreen />;
    case 'consent':
      return <ConsentScreen />;
    case 'intake':
      return <IntakeScreen />;
    case 'prepare':
      return <PrepareScreen />;
    case 'capture':
      return <CaptureScreen />;
    case 'qr':
      return <QrHandoffScreen />;
    case 'analysing':
      return <AnalyzingScreen />;
    case 'report':
      return <ReportScreen />;
    case 'recommendations':
      return <RecommendationsScreen />;
    case 'routine':
      return <RoutineScreen />;
    case 'lead':
      return <LeadCaptureScreen />;
    case 'booking':
      return <BookingScreen />;
    default:
      return <ErrorScreen code="UNKNOWN" message="Something went wrong." />;
  }
}

type Route =
  | { kind: 'widget'; key: string }
  | { kind: 'mobile'; sessionId: string; token: string };

function parseRoute(): Route {
  const segments = location.pathname.split('/').filter(Boolean);
  const params = new URLSearchParams(location.search);

  if (segments[0] === 'm' && segments[1]) {
    return { kind: 'mobile', sessionId: segments[1], token: params.get('t') ?? '' };
  }

  return { kind: 'widget', key: segments[1] ?? params.get('key') ?? '' };
}

/** Restores the report when someone opens a shared /r/:shortId link. */
export function useDeepLink(onReport: (shortId: string) => void): void {
  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments[0] === 'r' && segments[1]) onReport(segments[1]);
  }, [onReport]);
}
